import React, { useState, useEffect, lazy, Suspense } from 'react';

import { motion } from 'framer-motion';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';

import CustomSelect from '../../utils/CustomSelect';
import { formatCurrency } from '../../utils/Format';

const EtatsChart = lazy(() => import("../../components/charts/EtatsChart"));
const Chart3DWrapper = lazy(() => import("../../components/charts/Chart3DWrapper"));
const DoubleColumnChart = lazy(() => import("../../components/charts/DoubleColumnChart"));

const SiteTab = ({ sites, declarations = [], lstDocs = [], filters, setFilters, loadingS = false, loadingD = false }) => {

    const [filteredData, setFilteredData] = useState([]);

    const filterSites = (sites, filters) => {
        let filtered = sites;

        if (filters.societe !== "Toutes") {
            filtered = filtered.filter(d => d.Societe === filters.societe);
        }
        if (filters.entite !== "Toutes") {
            filtered = filtered.filter(d => d.Entite === filters.entite);
        }
        if (filters.etat !== "Toutes") {
            filtered = filtered.filter(d => d.Etat === filters.etat);
        }
        if (filters.type !== "Toutes") {
            filtered = filtered.filter(d => d.Type_Declaration === filters.type);
        }
        if (filters.typeOuvrage !== "Toutes") {
            filtered = filtered.filter(d => d.Type_Ouvrage === filters.typeOuvrage);
        }
        if (filters.typeDomaineActivite !== "Toutes") {
            filtered = filtered.filter(d => d.Domaine_Activite === filters.typeDomaineActivite);
        }
        if (filters.typeMarche !== "Toutes") {
            filtered = filtered.filter(d => d.Type_Marche === filters.typeMarche);
        }

        if (filters.minDate) {
            filtered = filtered.filter(d => new Date(d.Date_PVRecep_String) >= filters.minDate);
        }
        if (filters.maxDate) {
            filtered = filtered.filter(d => new Date(d.Date_PVRecep_String) <= filters.maxDate);
        }

        if (filters.withCost) {
            filtered = filtered.filter(d => d.Cout_tt_Pathologie > 0 || d.Montant_Du_Definitif > 0);
        }

        if (filters.hideArchived) {
            filtered = filtered.filter(d => !d.Archiver_Enr);
        }

        if (filters.hideCompleted) {
            const completedStates = ["cloture", "sans suite"];
            filtered = filtered.filter(d =>
                !completedStates.some(state =>
                    d.Etat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').startsWith(state)
                )
            );
        }

        return filtered;
    };

    useEffect(() => {
        const result = filterSites(sites, filters);
        setFilteredData(result);
    }, [filters, sites]);

    const optSociete = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Societe).filter(societe => societe))];
    const optEntite = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Entite).filter(entite => entite))];
    const optEtat = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Etat).filter(etat => etat))];
    const optType = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Declaration).filter(type => type))];
    const optDomaineActivite = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Domaine_Activite).filter(type => type))];
    const optTypeMarche = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Marche).filter(type => type))];

    const [top10ExpertiseBySite, setTop10ExpertiseBySite] = useState([]);
    const [top15CoutSites, setTop15CoutSites] = useState([]);

    const currentYear = new Date().getFullYear();
    const previousYear = currentYear - 1;

    const [totalDeclarationsCurrentYear, setTotalDeclarationsCurrentYear] = useState(0);
    const [totalPathologiesCurrentYear, setTotalPathologiesCurrentYear] = useState(0);
    const [totalDeclarationsPreviousYear, setTotalDeclarationsPreviousYear] = useState(0);
    const [totalPathologiesPreviousYear, setTotalPathologiesPreviousYear] = useState(0);

    useEffect(() => {
        const filterDeclarationsByYear = (year) =>
            declarations.filter(decla => {
                if (!decla.Date_Declaration) return false;
                const parsed = new Date(decla.Date_Declaration);
                return !isNaN(parsed) && parsed.getFullYear() === year;
            });

        const declasCurrent = filterDeclarationsByYear(currentYear);
        const declasPrevious = filterDeclarationsByYear(previousYear);

        setTotalPathologiesCurrentYear(
            Math.round(declasCurrent.reduce((acc, decla) => acc + (decla.Cout_tt_Pathologie || 0), 0))
        );
        setTotalDeclarationsCurrentYear(
            Math.round(declasCurrent.reduce((acc, decla) => acc + (decla.Montant_Du_Definitif || 0), 0))
        );
        setTotalPathologiesPreviousYear(
            Math.round(declasPrevious.reduce((acc, decla) => acc + (decla.Cout_tt_Pathologie || 0), 0))
        );
        setTotalDeclarationsPreviousYear(
            Math.round(declasPrevious.reduce((acc, decla) => acc + (decla.Montant_Du_Definitif || 0), 0))
        );
    }, [declarations]);

    const pathoDiff = totalPathologiesCurrentYear - totalPathologiesPreviousYear;
    const montantDiff = totalDeclarationsCurrentYear - totalDeclarationsPreviousYear;
    const pathoDiffPercent = Math.round(totalPathologiesPreviousYear !== 0 ? (pathoDiff / totalPathologiesPreviousYear) * 100 : 0);
    const montantDiffPercent = Math.round(totalDeclarationsPreviousYear !== 0 ? (montantDiff / totalDeclarationsPreviousYear) * 100 : 0);

    useEffect(() => {

        const filteredDocs = lstDocs
            .filter(doc =>
                doc._Type_Document === "Expertise" &&
                doc.Date_Convoc &&
                doc.Date_Convoc !== "0000-00-00"
            );

        const filteredSites = filterSites(sites, filters);

        const filterSitesByYear = (year) =>
            filteredSites.filter(site => {
                if (!site.Date_PVRecep_String) return false;
                const parsed = new Date(site.Date_PVRecep_String);
                return !isNaN(parsed) && parsed.getFullYear() === year;
            });


        const mergedCosts = filteredSites
            .filter(site => (site.Total_A_Payer || site.Total_Pathologie))
            .map(site => ({
                x: site.Nom || 'Inconnu',
                Total_A_Payer: site.Total_A_Payer || 0,
                Total_Pathologie: site.Total_Pathologie || 0
            }))
            .sort((a, b) =>
                (Math.abs(b.Total_A_Payer) + Math.abs(b.Total_Pathologie)) -
                (Math.abs(a.Total_A_Payer) + Math.abs(a.Total_Pathologie))
            )
            .slice(0, 15);

        const top15Costs = [];

        mergedCosts.forEach(site => {
            top15Costs.push({ x: site.x, y: site.Total_A_Payer, category: 'Déclarations' });
            top15Costs.push({ x: site.x, y: site.Total_Pathologie, category: 'Pathologies' });
        });

        setTop15CoutSites(top15Costs);

        const siteUUIDs = filteredSites.map(s => s.UUID_);

        const countBySite = {};

        filteredDocs.forEach(doc => {
            if (siteUUIDs.includes(doc.UUID_Site_)) {
                countBySite[doc.UUID_Site_] = (countBySite[doc.UUID_Site_] || 0) + 1;
            }
        });

        const topData = Object.entries(countBySite)
            .map(([uuid, count]) => {
                const site = sites.find(s => s.UUID_ === uuid);
                return {
                    x: site?.Nom || 'Inconnu',
                    y: count
                };
            })
            .sort((a, b) => b.y - a.y)
            .slice(0, 10);

        const safeTop10ExpertiseBySite = topData
            .filter(item => {
                const isValidX = typeof item.x === 'string' && item.x.trim() !== '';
                const isValidY = typeof item.y === 'number' && !isNaN(item.y);
                return isValidX && isValidY;
            });
        setTop10ExpertiseBySite(safeTop10ExpertiseBySite);


    }, [lstDocs, sites, filters]);

    return (
        <>
            <div className="bg-white mt-4 md:mt-8 p-4 rounded-lg">
                <div id="cont_filtre" className="flex xl:flex-row flex-col gap-4 w-full">
                    <div className="flex md:flex-row flex-col gap-4 w-full xl:w-1/2">
                        <div className="w-full">
                            <CustomSelect
                                label={"Société"}
                                options={optSociete}
                                value={filters.societe}
                                onChange={val => setFilters(prev => ({ ...prev, societe: val }))}
                            />
                            <CustomSelect
                                label={"Entité/Région"}
                                options={optEntite}
                                value={filters.entite}
                                onChange={val => setFilters(prev => ({ ...prev, entite: val }))}
                            />
                        </div>
                        <div className="w-full">
                            <CustomSelect
                                label={"État"}
                                options={optEtat}
                                value={filters.etat}
                                onChange={val => setFilters(prev => ({ ...prev, etat: val }))}
                            />
                            <CustomSelect
                                label={"Type Domaine d'Activité"}
                                options={optDomaineActivite}
                                value={filters.typeDomaineActivite}
                                onChange={val => setFilters(prev => ({ ...prev, typeDomaineActivite: val }))}
                            />
                        </div>
                    </div>
                    <div className="flex md:flex-row flex-col items-center gap-4 w-full xl:w-1/2 md:full">
                        <div className="flex flex-col justify-between h-full">
                            <div>
                                <span className="text-gray-600 text-sm">À partir du :</span>
                                <Suspense fallback={<div>Loading...</div>}>
                                    <DatePickerComponent
                                        placeholder="Trier par Date Minimal"
                                        format={"dd/MM/yyyy"}
                                        value={filters.minDate}
                                        change={(e) => setFilters(prev => ({ ...prev, minDate: e.value }))}
                                    />
                                </Suspense>
                            </div>
                            <div>
                                <span className="text-gray-600 text-sm">Jusqu'au :</span>
                                <Suspense fallback={<div>Loading...</div>}>
                                    <DatePickerComponent
                                        placeholder="Trier par Date Maximal"
                                        format={"dd/MM/yyyy"}
                                        value={filters.maxDate}
                                        change={(e) => setFilters(prev => ({ ...prev, maxDate: e.value }))}
                                    />
                                </Suspense>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="lg:flex items-stretch mt-4 md:mt-8">
                <div className="lg:w-1/2">
                    <div className="sm:flex justify-between items-center gap-x-6 xl:gap-x-8">
                        <div className="relative w-full">
                            <div className="bg-white bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4 rounded-lg w-full">
                                <div class="p-5 h-full">
                                    <div class="flex justify-between">
                                        <div class="text-white text-center">
                                            <i class="fa-solid fa-city fa-lg"></i>
                                        </div>
                                        <div
                                            class={`flex justify-items-center px-2 rounded-full h-6 font-semibold text-white bg-white text-sm ${loadingS ? 'animate-pulse' : ''}`}>
                                            {/* <span class="flex items-center">30%</span> */}
                                        </div>
                                    </div>
                                    <div class="flex-1 ml-2 w-full">
                                        <div>
                                            <div class="mt-3 font-bold text-white text-3xl leading-8">{loadingS ? <div class="bg-gray-100 rounded-md w-20 h-8 animate-pulse"></div> : filteredData.length}</div>

                                            <div class="mt-1 text-gray-200 text-base">Sites</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <motion.div
                        className="bg-white mt-4 md:mt-8 p-4 rounded-lg"
                    >
                        <Suspense fallback={<div>Loading...</div>}>
                            {(<EtatsChart data={filteredData} title="Répartition des Sites par Etats" />)}
                        </Suspense>
                    </motion.div>
                </div>
                <div className="lg:flex flex-col justify-between mt-4 md:mt-6 lg:mt-0 lg:ml-4 xl:ml-8 lg:w-1/2">
                    <div className="relative w-full">
                        <div className="bg-white p-4 rounded-lg w-full">
                            <Suspense fallback={<div>Loading...</div>}>
                                <Chart3DWrapper dataSource={top10ExpertiseBySite} title="Top 10 Du nombre d'expertises par Site" />
                            </Suspense>
                        </div>
                    </div>
                    <div className="sm:flex justify-between items-center gap-x-6 xl:gap-x-8 mt-4 md:mt-6">
                        <div className="relative mt-4 sm:mt-0 w-full">
                            <div className="flex flex-col justify-center items-center gap-2 bg-white bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4 rounded-lg w-full h-60 text-white">
                                <p class="font-bold text-2xl text-center uppercase">
                                    Année {currentYear}
                                </p>
                                <p class="font-medium text-sm text-center uppercase tracking-widest">
                                    Coûts Pathologies
                                </p>
                                <h6 class="font-bold text-white text-3xl leading-8">
                                    {loadingD ? <div class="bg-gray-100 rounded-md w-36 h-8 animate-pulse"></div>
                                        : <li>{formatCurrency(totalPathologiesCurrentYear)}
                                            {/* <span class={`${pathoDiffPercent > 0 ? "text-green-500" : "text-red-500"} text-base ml-2`}>({pathoDiffPercent} %)</span> */}
                                        </li>}
                                </h6>
                                <p class="mt-2 font-medium text-sm text-center uppercase tracking-widest">
                                    Coûts Déclarations
                                </p>
                                <h6 class="font-bold text-white text-3xl leading-8">
                                    {loadingD ? <div class="bg-gray-100 rounded-md w-36 h-8 animate-pulse"></div>
                                        : <li>{formatCurrency(totalPathologiesCurrentYear)}
                                            {/* <span class={`${montantDiffPercent > 0 ? "text-green-500" : "text-red-500"} text-base ml-2`}>({montantDiffPercent} %)</span> */}
                                        </li>}
                                </h6>
                            </div>
                        </div>
                        <div className="relative mt-4 sm:mt-0 w-full">
                            <div className="flex flex-col justify-center items-center gap-2 bg-white bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4 p-4 rounded-lg w-full h-60 text-white">
                                <p class="font-bold text-2xl text-center uppercase">
                                    Année {previousYear}
                                </p>
                                <p class="font-medium text-sm text-center uppercase tracking-widest">
                                    Coûts Pathologies
                                </p>
                                <h6 class="font-bold text-white text-3xl leading-8">
                                    {loadingD ? <div class="bg-gray-100 rounded-md w-36 h-8 animate-pulse"></div>
                                        : formatCurrency(totalPathologiesPreviousYear)}
                                </h6>
                                <p class="mt-2 font-medium text-sm text-center uppercase tracking-widest">
                                    Coûts Déclarations
                                </p>
                                <h6 class="font-bold text-white text-3xl leading-8">
                                    {loadingD ? <div class="bg-gray-100 rounded-md w-36 h-8 animate-pulse"></div>
                                        : formatCurrency(totalDeclarationsPreviousYear)}
                                </h6>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="lg:flex items-stretch mt-4 md:mt-8">
                <div className="bg-white p-4 rounded-lg w-full">
                    <Suspense fallback={<div>Loading...</div>}>
                        <DoubleColumnChart id="DoubleChart02" data={top15CoutSites} title="Top 15 Coûts Déclarations & Pathologies par Site" />
                    </Suspense>
                </div>
            </div>
        </>
    );
}

export default SiteTab;